import classNames from "classnames";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
// react
import { useNavigate, useParams } from "react-router-dom";
// packages
import { AlertColor } from "@mui/material";
import { useTranslation } from "react-i18next";

// events
import Spinner from "../../components/common/Spinner";
// api calls
import {
  getContractActions,
  getContractDetails,
} from "../../setup/api/services/contract";
// types
import {
  EContractType,
  ESenderRole,
} from "../../setup/api/services/contract/models";
import { ROUTES } from "../../setup/constants";
// context providers
import { AuthConfig } from "../../setup/context/config/authConfig";
import { getAccessToken } from "../../setup/auth/oidc";
// contract context
import {
  getContractAction,
  getContractActivities,
  setContractType,
} from "./contract.actions";
import { ContractContext } from "./contract.context";
import { CONTRACT_INITIAL_STATE, contractReducer } from "./contract.reducer";
// styles
import styles from "./styles.module.scss";
import CustomizedSnackbars from "../../components/common/customizedSnackbars";
import { EContractActions } from "../../setup/api/services/contractActions/enum";
import { enrichPartiesProfilesPhoneNumbers } from "../../setup/util/helpers";
import { doAction } from "../../setup/api/services/contractActions";
import { ampli } from "../../ampli";
import { ECONTRACT_STATUS } from "../../enums";

type Props = {
  children: React.ReactNode;
};

// eslint-disable-next-line react/display-name
export const ContractProvider = React.memo(
  ({ children }: Props): JSX.Element => {
    const { t } = useTranslation();
    const [isAuthenticated] = useState<boolean>(true);
    const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
    const [snackBarColor, setSnackBarColor] = useState<AlertColor>("info");
    const [snackBarTitle, setSnackBarTitle] = useState<string>("");
    const [actorType, setActorType] = useState<ESenderRole>();
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const navigate = useNavigate();
    const { id } = useParams();
    const userId = useMemo(() => {
      return AuthConfig?.getUserInfo()?.id;
    }, []);
    const userIdentification = useMemo(() => {
      return AuthConfig?.getUserInfo()?.userIdentification;
    }, []);
    const urlParams = new URLSearchParams(window?.location?.search);
    const userType = urlParams?.get("userType");

    const decodeToken = useCallback((token) => {
      try {
        const base64Url = token.split(".")[1]; // Get the payload part of the token
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join("")
        );
        const data = JSON.parse(jsonPayload);
        // Keycloak puts roles in realm_access.roles; the legacy token used authorities.
        const notAnAdmin = (
          data?.authorities ?? data?.realm_access?.roles
        )?.some((role) => role === "CLIENT_ADMIN");
        setIsAdmin(!notAnAdmin);
      } catch (error) {
        // Fail safe: treat as non-admin on decode failure
        setIsAdmin(false);
      }
    }, []);

    useEffect(() => {
      const checkToken = async () => {
        const userToken = await getAccessToken();
        if (userToken) {
          decodeToken(userToken);
        } else {
          setIsAdmin(false);
        }
      };
      void checkToken();
    }, [decodeToken]);

    // states
    const [loading, setLoading] = useState<boolean>(id ? true : false);

    const [state, dispatch] = useReducer(
      contractReducer,
      CONTRACT_INITIAL_STATE
    );

    const brokerSellerId = useMemo(() => {
      return state?.contract?.partiesProfiles?.PROVIDER?.[0]?.id;
    }, [state?.contract?.partiesProfiles]);

    const brokerCustomerId = useMemo(() => {
      return state?.contract?.partiesProfiles?.CUSTOMER?.[0]?.id;
    }, [state?.contract?.partiesProfiles]);

    // init actions with dispatcher
    const getContract = getContractAction(dispatch);
    const setContract = setContractType(dispatch);
    const getActivities = getContractActivities(dispatch);

    useEffect(() => {
      if (snackBarOpen) {
        setTimeout(() => {
          setSnackBarOpen(false);
          setSnackBarColor("info");
          setSnackBarTitle("");
        }, 5000);
      }
    }, [snackBarOpen]);

    const renderSnackBar = () => {
      return snackBarOpen ? (
        <CustomizedSnackbars
          open={snackBarOpen}
          title={snackBarTitle}
          type={snackBarColor}
        />
      ) : null;
    };

    const pausedState = useMemo(() => {
      const quantity = state?.contract?.quantity || 0;
      return (
        state?.contract?.status === ECONTRACT_STATUS.OUT_OF_STOCK &&
        quantity > 0
      );
    }, [state?.contract?.quantity, state?.contract?.status]);

    const getActorType = useCallback(() => {
      const brokers = state?.contract?.partiesProfiles?.BROKER;
      const customers = state?.contract?.partiesProfiles?.CUSTOMER;
      const senders = state?.contract?.partiesProfiles?.PROVIDER;

      if (
        brokers?.length &&
        brokers.some((broker) => String(broker?.id) == userId)
      )
        setActorType(ESenderRole.BROKER);
      if (
        brokers?.length &&
        customers?.some((customer) => String(customer?.id) == userId)
      )
        setActorType(ESenderRole.CUSTOMER);
      if (
        brokers?.length &&
        senders?.some((sender) => String(sender?.id) == userId)
      )
        setActorType(ESenderRole.PROVIDER);

      if (
        state?.contract?.senderId == userId ||
        state?.contract?.senderId == userIdentification
      )
        setActorType(state?.contract?.senderRole);
      else
        setActorType(
          userType === "seller" ||
            state?.contract?.senderRole === ESenderRole.CUSTOMER
            ? ESenderRole.PROVIDER
            : userType === "buyer" ||
              state?.contract?.senderRole === ESenderRole.PROVIDER
            ? ESenderRole.CUSTOMER
            : ESenderRole.CUSTOMER
        );
    }, [
      state?.contract?.partiesProfiles?.BROKER,
      state?.contract?.partiesProfiles?.CUSTOMER,
      state?.contract?.partiesProfiles?.PROVIDER,
      state?.contract?.senderId,
      state?.contract?.senderRole,
      userId,
      userIdentification,
      userType,
    ]);

    useEffect(() => {
      if (state?.contract) {
        getActorType();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state?.contract]);

    const fetchContractDetails = useCallback(
      async (id: string) => {
        setLoading(true);
        try {
          const response = await getContractDetails(id);
          const contractData = response?.data;
          // Backfill missing partiesProfiles phone numbers from `parties`.
          enrichPartiesProfilesPhoneNumbers(contractData);
          setContract(contractData?.type);
          if (
            userId == contractData.senderId ||
            (contractData.receiverId && userId == contractData?.receiverId) ||
            !contractData.receiverId ||
            +userId == contractData?.partiesProfiles?.BROKER?.[0]?.id ||
            (contractData?.partiesProfiles?.BROKER?.[0]?.id &&
              !contractData?.senderId)
          ) {
            getContract(contractData);
            setLoading(false);
          } else {
            setLoading(false);
            getContract(contractData);
          }
        } catch (error) {
          navigate(ROUTES.CONTRACT_NOT_FOUND);
          setTimeout(() => setLoading(false), 500);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [navigate, userId, brokerSellerId, brokerCustomerId]
    );

    const fetchContractActions = useCallback(
      async (id: string) => {
        setLoading(true);
        try {
          const result = await getContractActions(id);
          const contractActivities = result?.data;
          getActivities(contractActivities);
          setLoading(false);
        } catch (error) {
          setLoading(false);
        }
      },
      [getActivities]
    );

    const refreshContract = useCallback(() => {
      if (id) {
        fetchContractDetails(id);
        fetchContractActions(id);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
      if (id && isAuthenticated) {
        fetchContractDetails(id);
        fetchContractActions(id);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchContractDetails, isAuthenticated, id]);

    const endTransactionForBuyer = useCallback(
      async (contractId?: string) => {
        if (!state?.contract?.id && !contractId) return;
        setLoading(true);
        try {
          const body = {
            contractId: contractId || state?.contract?.id || "",
            userId: userId,
            actorRole: ESenderRole.BROKER,
            contractAction:
              state?.contract?.type === EContractType.COMPLEX_CONTRACT
                ? EContractActions.ARBITRATOR_RESOLVE_REFUND_CUSTOMER
                : EContractActions.BROKER_REFUND_CUSTOMER,
          };
          await doAction(body);
          ampli.endedBrokerContract({
            contractId: contractId || state?.contract?.id || "",
            brokerContractClosure: "Refund",
          });
          setSnackBarOpen(true);
          setSnackBarTitle(`${t("lbl_success_message")}`);
          setSnackBarColor("success");
          refreshContract();
          setLoading(false);
        } catch (error) {
          setSnackBarOpen(true);
          setSnackBarTitle(`${t("lbl_error_message")}`);
          setSnackBarColor("error");
          setLoading(false);
        }
      },
      [refreshContract, state?.contract?.id, state?.contract?.type, t, userId]
    );

    const endTransactionForSeller = useCallback(
      async (contractId?: string) => {
        if (!state?.contract?.id && !contractId) return;
        setLoading(true);
        try {
          const body = {
            contractId: contractId || state?.contract?.id || "",
            userId: userId,
            actorRole: ESenderRole.BROKER,
            contractAction:
              state?.contract?.type === EContractType.COMPLEX_CONTRACT
                ? EContractActions.ARBITRATOR_CONFIRM_DELIVERY
                : EContractActions.BROKER_ACCEPT_ITEM,
          };
          await doAction(body);
          ampli.endedBrokerContract({
            contractId: contractId || state?.contract?.id || "",
            brokerContractClosure: "Release",
          });
          setSnackBarOpen(true);
          setSnackBarTitle(`${t("lbl_success_message")}`);
          setSnackBarColor("success");
          refreshContract();
          setLoading(false);
        } catch (error) {
          setSnackBarOpen(true);
          setSnackBarTitle(`${t("lbl_error_message")}`);
          setSnackBarColor("error");
          setLoading(false);
        }
      },
      [refreshContract, state?.contract?.id, state?.contract?.type, t, userId]
    );

    const resetContractState = useCallback(() => {
      getContract(undefined);
      setContract(undefined);
    }, [getContract, setContract]);

    const value = useMemo(
      (): ContractContext =>
        ({
          ...state,
          userType,
          actorType,
          pausedState,
          actions: {
            getContract: () => {
              id && fetchContractDetails(id);
            },
            showSnackbar: (title: string, color: AlertColor) => {
              setSnackBarOpen(true);
              setSnackBarTitle(title);
              setSnackBarColor(color);
              refreshContract();
            },
            endTransactionSeller: (contractId?: string) => {
              endTransactionForSeller(contractId);
            },
            endTransactionBuyer: (contractId?: string) => {
              endTransactionForBuyer(contractId);
            },
            resetContractState: () => {
              resetContractState();
            },
          },
        } as ContractContext),

      [state, userType, actorType, pausedState] // eslint-disable-line react-hooks/exhaustive-deps
    );

    if (loading) {
      return (
        <div
          className={classNames(
            { [styles.container]: !isAdmin },
            { [styles.containerAdmin]: isAdmin },
            styles.center
          )}>
          <Spinner />
        </div>
      );
    }

    return (
      <ContractContext.Provider value={value}>
        <Fragment>{children}</Fragment>
        {renderSnackBar()}
      </ContractContext.Provider>
    );
  }
);

export default ContractProvider;
